import React, { Suspense, lazy, useEffect, useState } from 'react';
import { CanEdit } from "@/shared/readOnlyManager";
import { Container } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from "react-redux";
import ReactTooltip from 'react-tooltip';
import axios from 'axios';
import { amountToUsFormat } from '@/shared/helpers';

import {
  getDashboardTodaysAwards,
  getDashboardTodaysRedumptions,
  getDashboardTodaysActiveParticipants
} from '@/services/program/getDashboard';

const TodayItem = lazy(() => import('../components/TodayItem'));
const TopPanelMerchant = lazy(() => import('../components/TopPanelMerchant'));
const TopPanelAward = lazy(() => import('../components/TopPanelAward'));
const DetailChartItemAward = lazy(() => import('../components/DetailChartItemAward'));
const PeerActivitySummary = lazy(() => import('../components/PeerActivitySummary'));
const PendingActivationWidget = lazy(() => import('../components/PendingActivationWidget'));
const PendingApprovalsWidget = lazy(() => import('../components/PendingApprovalsWidget'));
const HeroInsight = lazy(() => import('../components/HeroInsight'));

const EMPTY_METRIC = { value: 0, amount: 0 };
const INITIAL_METRIC = { today: null, mtd: null, ytd: null };

const getMetricValue = (result, rootKey, rangeKey, fallback) => {
  if (result.status !== "fulfilled") {
    return fallback;
  }

  if (!rangeKey) {
    return result.value?.[rootKey] ?? fallback;
  }

  return result.value?.[rootKey]?.[rangeKey] ?? fallback;
};

const getLoadingCard = (label) => (
  <div className="manager-card manager-card--loading">
    <span>{label}</span>
  </div>
);

const formatPoints = (value) => amountToUsFormat(Number(value) || 0, false);
const formatCurrency = (value) => amountToUsFormat(Number(value) || 0, true, "$");

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getInvoicePaymentInfo = (invoice) => {
  if (!invoice || !invoice.journal_events || invoice.journal_events.length === 0) {
    return { isPaid: false, datePaid: null };
  }
  
  const paymentEvent = invoice.journal_events.find(event => {
    const typeName = event.journal_event_type?.type || '';
    return typeName.includes('Program pays for') || typeName.includes('Program refunds for');
  });
  
  if (paymentEvent) {
    return {
      isPaid: true,
      datePaid: paymentEvent.created_at
    };
  }
  
  return { isPaid: false, datePaid: null };
};

const getInvoiceAmount = (invoice) => {
  if (!invoice) return 0;
  // gross_invoice_amount is the amount actually charged, excluding "Program pays
  // for..." payment credits - unlike custom_invoice_amount/total_end_balance for
  // On-Demand/Credit Card invoices, it doesn't net to 0 once the invoice is paid.
  if (invoice.gross_invoice_amount != null && invoice.gross_invoice_amount !== '') {
    return Math.abs(Number(invoice.gross_invoice_amount));
  }
  if (invoice.amount != null && invoice.amount !== '') {
    return Math.abs(Number(invoice.amount));
  }
  if (invoice.custom_invoice_amount != null && invoice.custom_invoice_amount !== '') {
    return Math.abs(Number(invoice.custom_invoice_amount));
  }
  const invoiceType = invoice.invoice_type?.name || '';
  if (invoiceType === 'On-Demand' || invoiceType === 'Credit Card Deposit') {
    return Math.abs(Number(invoice.total_end_balance || 0));
  }
  if (invoice.journal_summary?.grand_total != null) {
    return Math.abs(Number(invoice.journal_summary.grand_total));
  }
  return Math.abs(Number(invoice.total_end_balance || 0));
};

const Dashboard = ({ organization, program }) => {
  const [awardsToday, setAwardsToday] = useState(INITIAL_METRIC);
  const [redemptionToday, setRedemptionToday] = useState(INITIAL_METRIC);
  const [participantToday, setParticipantToday] = useState(INITIAL_METRIC);

  // A program shows the parent-pays layout when it has billable descendants
  // (subprograms configured to bill their charges up to this program), not
  // based on its own billing flags - those describe its relationship to its
  // OWN parent, not to any children.
  const [showNewLayout, setShowNewLayout] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [unpaidCount, setUnpaidCount] = useState(null);

  useEffect(() => {
    if (!organization?.id || !program?.id) {
      setShowNewLayout(false);
      return;
    }

    let isActive = true;
    axios.get(`/organization/${organization.id}/program/${program.id}/dashboard/parent-pays-status`)
      .then(res => {
        if (isActive) setShowNewLayout(!!res.data?.has_billable_descendants);
      })
      .catch(err => {
        console.error("Failed to fetch parent-pays status", err);
        if (isActive) setShowNewLayout(false);
      });

    return () => {
      isActive = false;
    };
  }, [organization?.id, program?.id]);

  useEffect(() => {
    if (!organization?.id || !program?.id) return;

    let isActive = true;

    setAwardsToday(INITIAL_METRIC);
    setRedemptionToday(INITIAL_METRIC);
    setParticipantToday(INITIAL_METRIC);

    Promise.allSettled([
      getDashboardTodaysAwards(organization.id, program.id, 'today'),
      getDashboardTodaysAwards(organization.id, program.id, 'mtd'),
      getDashboardTodaysAwards(organization.id, program.id, 'ytd'),
      getDashboardTodaysRedumptions(organization.id, program.id, 'today'),
      getDashboardTodaysRedumptions(organization.id, program.id, 'mtd'),
      getDashboardTodaysRedumptions(organization.id, program.id, 'ytd'),
      getDashboardTodaysActiveParticipants(organization.id, program.id),
    ]).then((results) => {
      if (!isActive) return;

      const [
        awardsTodayResult,
        awardsMtdResult,
        awardsYtdResult,
        redemptionsTodayResult,
        redemptionsMtdResult,
        redemptionsYtdResult,
        participantsResult,
      ] = results;

      setAwardsToday({
        today: getMetricValue(awardsTodayResult, "awardsToday", "today", EMPTY_METRIC),
        mtd: getMetricValue(awardsMtdResult, "awardsToday", "mtd", EMPTY_METRIC),
        ytd: getMetricValue(awardsYtdResult, "awardsToday", "ytd", EMPTY_METRIC),
      });

      setRedemptionToday({
        today: getMetricValue(redemptionsTodayResult, "redemptionToday", "today", EMPTY_METRIC),
        mtd: getMetricValue(redemptionsMtdResult, "redemptionToday", "mtd", EMPTY_METRIC),
        ytd: getMetricValue(redemptionsYtdResult, "redemptionToday", "ytd", EMPTY_METRIC),
      });

      const participantMetrics = getMetricValue(participantsResult, "participantToday", null, {});
      setParticipantToday({
        today: participantMetrics?.today ?? { value: 0 },
        mtd: participantMetrics?.mtd ?? { value: 0 },
        ytd: participantMetrics?.ytd ?? { value: 0 },
        type: "users",
      });
    });

    return () => {
      isActive = false;
    };
  }, [organization?.id, program?.id]);

  const isPayInAdvance = program ? (program.is_pay_in_advance === 1 || program.is_pay_in_advance === true || program.is_pay_in_advance === '1') : false;

  useEffect(() => {
    if (!organization?.id || !program?.id || !showNewLayout || !isPayInAdvance) {
      setUnpaidCount(null);
      return;
    }

    let isActive = true;
    axios.get(`/organization/${organization.id}/program/${program.id}/invoice/unpaid-count`)
      .then(res => {
        if (isActive) setUnpaidCount(res.data?.count ?? null);
      })
      .catch(err => {
        console.error("Failed to fetch unpaid invoice count", err);
        if (isActive) setUnpaidCount(null);
      });

    return () => {
      isActive = false;
    };
  }, [organization?.id, program?.id, showNewLayout, isPayInAdvance]);

  useEffect(() => {
    if (!organization?.id || !program?.id || !showNewLayout) return;

    setInvoicesLoading(true);

    // Parent-pays charges from billable descendants are already rolled up into
    // this program's own invoices at generation time, so fetch this program's
    // invoices directly - same scope Admin's Invoices listing uses.
    axios.get(`/organization/${organization.id}/program/${program.id}/invoice?page=1&limit=50`)
      .then(res => {
        const results = res.data?.data
          ? Object.values(res.data.data)
          : (Array.isArray(res.data) ? res.data : []);

        // Sort results by date_due or created_at desc to make sure recent ones are first
        results.sort((a, b) => new Date(b.date_due || b.created_at) - new Date(a.date_due || a.created_at));

        const top3Invoices = results.slice(0, 3);

        // Fetch detailed compilation for the top 3 invoices in parallel
        const detailPromises = top3Invoices.map(inv =>
          axios.get(`/organization/${organization.id}/program/${inv.program_id || program.id}/invoice/${inv.id}`)
            .then(detailRes => detailRes.data)
            .catch(err => {
              console.error(`Failed to load detailed compile for invoice ${inv.id}`, err);
              return inv; // fallback to basic invoice info
            })
        );

        return Promise.all(detailPromises).then(detailedInvoices => {
          setInvoices(detailedInvoices);
          setInvoicesLoading(false);
        });
      })
      .catch(err => {
        console.error("Failed to fetch invoices", err);
        setInvoicesLoading(false);
      });
  }, [organization?.id, program?.id, showNewLayout]);

  const hasPeerAwards = program?.uses_peer2peer > 0;

  useEffect(() => {
    const timeoutId = setTimeout(() => ReactTooltip.rebuild(), 0);
    return () => clearTimeout(timeoutId);
  }, [awardsToday, redemptionToday, participantToday]);

  const navigate = useNavigate();
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const isStatsLoading = awardsToday?.today?.value == null || redemptionToday?.today?.value == null || participantToday?.today?.value == null;

  return (
    <Container className="manager-home__shell manager-dashboard">
      {!showNewLayout && (
        <Suspense fallback={null}>
          <HeroInsight
            awardsToday={awardsToday}
            redemptionToday={redemptionToday}
            participantToday={participantToday}
          />
        </Suspense>
      )}

      <div className="manager-section-label">
        <div className="manager-section-label__left">
          <span>Today's Overview</span>
          <time>{today}</time>
        </div>
        <CanEdit>
          <button
            type="button"
            data-tip="Go to Manage & Reward to send a reward to participants"
            className="manager-home__send-btn"
            onClick={() => navigate('/manager/manage-and-reward')}
          >
            Send Reward
          </button>
        </CanEdit>
      </div>

      {showNewLayout ? (
        <section className="manager-dashboard__combined-card">
          <div className="manager-dashboard__combined-left">
            {isStatsLoading ? (
              <div className="manager-metric-card__loading" style={{ padding: '1rem' }}>
                <div className="skeleton skeleton-line" style={{ width: "150px", height: "24px", marginBottom: "1rem" }} />
                <div className="skeleton skeleton-line" style={{ width: "100px", height: "40px", marginBottom: "2rem" }} />
                <div className="skeleton skeleton-line" style={{ width: "150px", height: "24px", marginBottom: "1rem" }} />
                <div className="skeleton skeleton-line" style={{ width: "100px", height: "40px" }} />
              </div>
            ) : (
              <>
                {/* Points Awarded */}
                <div className="manager-dashboard__metric-row">
                  <div className="manager-dashboard__metric-info">
                    <span className="manager-dashboard__metric-label">Points Awarded</span>
                    <div className="manager-dashboard__metric-value-group">
                      <strong className="manager-dashboard__metric-value">{formatPoints(awardsToday.today?.value)}</strong>
                      <span className="manager-dashboard__metric-unit">pts today</span>
                    </div>
                    {awardsToday.today?.amount != null && (
                      <span className="manager-dashboard__metric-amount">{formatCurrency(awardsToday.today.amount)}</span>
                    )}
                  </div>
                  <div className="manager-dashboard__metric-stats">
                    <div className="manager-dashboard__metric-stat-item">
                      <span className="manager-dashboard__metric-stat-label">MTD</span>
                      <strong className="manager-dashboard__metric-stat-value">{formatPoints(awardsToday.mtd?.value)} pts</strong>
                    </div>
                    <div className="manager-dashboard__metric-stat-item">
                      <span className="manager-dashboard__metric-stat-label">YTD</span>
                      <strong className="manager-dashboard__metric-stat-value">{formatPoints(awardsToday.ytd?.value)} pts</strong>
                    </div>
                  </div>
                </div>

                {/* Points Redeemed */}
                <div className="manager-dashboard__metric-row">
                  <div className="manager-dashboard__metric-info">
                    <span className="manager-dashboard__metric-label">Points Redeemed</span>
                    <div className="manager-dashboard__metric-value-group">
                      <strong className="manager-dashboard__metric-value">{formatPoints(redemptionToday.today?.value)}</strong>
                      <span className="manager-dashboard__metric-unit">pts today</span>
                    </div>
                    {redemptionToday.today?.amount != null && (
                      <span className="manager-dashboard__metric-amount">{formatCurrency(redemptionToday.today.amount)}</span>
                    )}
                  </div>
                  <div className="manager-dashboard__metric-stats">
                    <div className="manager-dashboard__metric-stat-item">
                      <span className="manager-dashboard__metric-stat-label">MTD</span>
                      <strong className="manager-dashboard__metric-stat-value">{formatPoints(redemptionToday.mtd?.value)} pts</strong>
                    </div>
                    <div className="manager-dashboard__metric-stat-item">
                      <span className="manager-dashboard__metric-stat-label">YTD</span>
                      <strong className="manager-dashboard__metric-stat-value">{formatPoints(redemptionToday.ytd?.value)} pts</strong>
                    </div>
                  </div>
                </div>

                {/* Active Participants */}
                <div className="manager-dashboard__metric-row">
                  <div className="manager-dashboard__metric-info">
                    <span className="manager-dashboard__metric-label">Active Participants</span>
                    <div className="manager-dashboard__metric-value-group">
                      <strong className="manager-dashboard__metric-value">{formatPoints(participantToday.today?.value)}</strong>
                      <span className="manager-dashboard__metric-unit">active today</span>
                    </div>
                  </div>
                  <div className="manager-dashboard__metric-stats">
                    <div className="manager-dashboard__metric-stat-item">
                      <span className="manager-dashboard__metric-stat-label">MTD</span>
                      <strong className="manager-dashboard__metric-stat-value">{formatPoints(participantToday.mtd?.value)}</strong>
                    </div>
                    <div className="manager-dashboard__metric-stat-item">
                      <span className="manager-dashboard__metric-stat-label">YTD</span>
                      <strong className="manager-dashboard__metric-stat-value">{formatPoints(participantToday.ytd?.value)}</strong>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="manager-dashboard__combined-right">
            <div className="manager-dashboard__invoices-header">
              <h3>Invoices</h3>
              {isPayInAdvance && !!unpaidCount && (
                <span className="manager-dashboard__unpaid-badge">
                  {unpaidCount} unpaid
                </span>
              )}
            </div>
            {invoicesLoading ? (
              <div className="manager-metric-card__loading" style={{ padding: '1rem' }}>
                <div className="skeleton skeleton-line" style={{ width: "100%", height: "24px", marginBottom: "0.5rem" }} />
                <div className="skeleton skeleton-line" style={{ width: "100%", height: "24px", marginBottom: "0.5rem" }} />
                <div className="skeleton skeleton-line" style={{ width: "100%", height: "24px" }} />
              </div>
            ) : invoices.length === 0 ? (
              <div className="manager-billing__empty-state" style={{ padding: '2rem 1rem', border: '1px dashed #edf2f7', borderRadius: '6px' }}>
                <strong>No invoices found.</strong>
              </div>
            ) : (
              <>
                <div className="manager-dashboard__invoices-table-container">
                  <table className="manager-dashboard__invoices-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Inv. Number</th>
                        <th>Amount</th>
                        <th>Date Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => {
                        const paymentInfo = getInvoicePaymentInfo(invoice);
                        return (
                          <tr key={invoice.id}>
                            <td>{formatDate(invoice.date_end)}</td>
                            <td>{invoice.invoice_number}</td>
                            <td>{formatCurrency(getInvoiceAmount(invoice))}</td>
                            <td>
                              {paymentInfo.isPaid ? (
                                <span className="manager-dashboard__status-paid">
                                  {formatDate(paymentInfo.datePaid)}
                                </span>
                              ) : isPayInAdvance ? (
                                <span className="manager-dashboard__status-unpaid">
                                  Unpaid
                                </span>
                              ) : (
                                <span className="manager-dashboard__status-na">
                                  N/A
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  className="manager-dashboard__see-more-btn"
                  onClick={() => navigate('/manager/report/invoices')}
                >
                  See more invoices &gt;
                </button>
              </>
            )}
          </div>
        </section>
      ) : (
        <section className="manager-dashboard__stats">
          <TodayItem
            data={awardsToday}
            variant="awards"
            title="Points Awarded"
            tooltip="Total points awarded to participants today"
          />
          <TodayItem
            data={redemptionToday}
            variant="redemptions"
            title="Points Redeemed"
            tooltip="Total points redeemed by participants today"
          />
          <TodayItem
            data={participantToday}
            variant="participants"
            title="Active Participants"
            tooltip="Number of participants active today"
          />
        </section>
      )}

      <div className="manager-section-label manager-section-label--spaced">
        <span>Activity & Analytics</span>
      </div>
      <section className={`manager-dashboard__content${hasPeerAwards ? " manager-dashboard__content--peer" : ""}`}>
        <aside className="manager-dashboard__rail">
          <Suspense fallback={null}>
            <PendingActivationWidget />
          </Suspense>
          <Suspense fallback={null}>
            <PendingApprovalsWidget />
          </Suspense>
          <Suspense fallback={getLoadingCard("Loading top merchants...")}>
            <TopPanelMerchant />
          </Suspense>
          <Suspense fallback={getLoadingCard("Loading top awards...")}>
            <TopPanelAward />
          </Suspense>
        </aside>

        <div className="manager-dashboard__main">
          <Suspense fallback={getLoadingCard("Loading award activity...")}>
            <DetailChartItemAward />
          </Suspense>
          {hasPeerAwards && (
            <Suspense fallback={null}>
              <PeerActivitySummary />
            </Suspense>
          )}
        </div>
      </section>
    </Container>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  program: state.program,
  organization: state.organization,
});

export default connect(mapStateToProps)(Dashboard);
